/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */
import { useQuery } from '@tanstack/react-query';

const API_ENDPOINT = '/api/graphql';

const BUCKETS = ['0-10', '10-50', '50-100', '100-300', '300-1000', '1000+'];

async function fetchGraphQL(query: string, variables: Record<string, unknown>): Promise<unknown> {
  const res = await fetch(API_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (Boolean(json.errors?.length)) {
    const isTimeout = json.errors.some((e: { message?: string }): boolean | undefined =>
      e.message?.includes('TIMEOUT'),
    );
    if (Boolean(isTimeout)) throw new Error('AbortError');
    throw new Error(json.errors[0].message);
  }
  return json.data as unknown;
}

// -------------------------------------------------------------
// Hooks
// -------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- return type is UseQueryResult with complex generics
export function useProjectStats(projectId: string | undefined, range: string) {
  return useQuery({
    queryKey: ['stats', projectId, range],
    enabled: !(projectId == null),
    queryFn: async () => {
      const q = `
        query GetStats($id: ID!, $range: TimeRange!) {
          project(id: $id) {
            stats(range: $range) {
              total
              success
              error
              latencyBuckets
              isPartial
              bucketEnd
            }
          }
        }
      `;

      const data = await fetchGraphQL(q, { id: projectId, range });

      const stats = (data as Record<string, Record<string, Record<string, unknown>>>)?.[
        'project'
      ]?.['stats'];

      // Ensure strict chart bucket map natively
      const latencyBuckets = stats?.['latencyBuckets'] as Record<string, number> | undefined;
      const chartData = BUCKETS.map((b) => ({
        name: b,
        value: latencyBuckets ? Number(latencyBuckets[b] ?? 0) : 0,
      }));

      return {
        total: Number(stats?.['total'] ?? 0),
        success: Number(stats?.['success'] ?? 0),
        error: Number(stats?.['error'] ?? 0),
        isPartial: Boolean(stats?.['isPartial']),
        bucketEnd: stats?.['bucketEnd'] != null ? Number(stats['bucketEnd']) : undefined,
        latencyBuckets,
        chartData,
      };
    },
    // Keep UI smooth natively across bounds
    placeholderData: (prev) => prev,
    // Add explicitly jittered polling!
    refetchInterval: range === 'LAST_5_MIN' ? (): number => 3000 + Math.random() * 1000 : false,
    staleTime: range === 'LAST_5_MIN' ? 2000 : 60_000,
    retry: (failureCount, error): boolean => {
      if (error?.message === 'AbortError') return false;
      return failureCount < 2;
    },
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- return type is UseQueryResult with complex generics
export function useProjectExecutions(
  projectId: string | undefined,
  range: string,
  limit: number = 100,
) {
  return useQuery({
    queryKey: ['executions', projectId, range, limit],
    enabled: !(projectId == null),
    queryFn: async (): Promise<unknown[]> => {
      const q = `
        query GetExecs($id: ID!, $range: TimeRange!, $limit: Int!) {
          project(id: $id) {
            executions(range: $range, limit: $limit) {
              executionId
              receivedAt
              durationMs
              status
              entry
              isPartial
            }
          }
        }
      `;

      const data = await fetchGraphQL(q, { id: projectId, range, limit });

      return (data as Record<string, Record<string, unknown[]>>)['project']?.['executions'] ?? [];
    },
    placeholderData: (prev): unknown[] | undefined => prev,
    refetchInterval: range === 'LAST_5_MIN' ? 5000 : false,
    staleTime: range === 'LAST_5_MIN' ? 4000 : 60_000,
    retry: (failureCount, error): boolean => {
      if (error?.message === 'AbortError') return false;
      return failureCount < 1;
    },
  });
}
